import { cn } from '@/lib/utils';

export type CosmosMarkProps = {
  className?: string;
  /** Rendered box in px (square, `viewBox="0 0 24 24"`). Defaults to 24. */
  size?: number;
};

/**
 * CosmosMark - the real Cosmos AI product mark, marketing-local.
 *
 * R8 restructure contract (2026-08-12) §H + §B: the hero's convergence terminus
 * and the brain-system core must show the ACTUAL product identity, not a stand-in
 * glyph. The geometry below is the orbit/rays mark shipped in
 * `src/features/protocol-onboarding/components/ui/CosmosAvatar.tsx:68-91`
 * (`CosmosIcon`), copied path-for-path on purpose: the contract explicitly bans a
 * cross-feature import (`marketing` must not depend on `protocol-onboarding`, and
 * that component is a `'use client'` avatar with gold-gradient chrome and label
 * text this needs none of). One central circle plus eight rays, nothing else.
 *
 * Deliberately NOT a client component: pure presentational SVG, no state, no
 * effects, so it renders inside the SSR hero without adding a hydration boundary
 * to the LCP column (`check-marketing-lcp-h1`).
 *
 * Color comes from `currentColor` and stroke weight is fixed at the editorial
 * hairline (1.5 instead of the in-app 1.5x-heavier 2) so the mark sits in the R7
 * paper/dark registries without ever hardcoding a hex here - the caller sets ink
 * via a registry token class (`PAPER_INK.*`, `DARK_INK.*`, or the amber accent).
 *
 * `aria-hidden`: every call site pairs the mark with the visible "CMG Workflow" /
 * "Cosmos AI" wordmark from `LANDING_CONTENT`, so announcing it again would
 * double the name for screen readers.
 */
export function CosmosMark({ className, size = 24 }: CosmosMarkProps) {
  return (
    <svg
      data-primitive="CosmosMark"
      aria-hidden="true"
      focusable="false"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn('shrink-0', className)}
    >
      {/* Core */}
      <circle cx="12" cy="12" r="3" />
      {/* Rays - vertical / horizontal / two diagonal pairs */}
      <path d="M12 2v4" />
      <path d="M12 18v4" />
      <path d="M4.93 4.93l2.83 2.83" />
      <path d="M16.24 16.24l2.83 2.83" />
      <path d="M2 12h4" />
      <path d="M18 12h4" />
      <path d="M4.93 19.07l2.83-2.83" />
      <path d="M16.24 7.76l2.83-2.83" />
    </svg>
  );
}
